import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Document } from '@app/core/model/entities/document/document';
import { Entity } from '@app/core/model/entities/entity';
import { RelatedEquipment } from '@app/core/model/entities/equipments/equipment';
import { RelatedProject } from '@app/core/model/entities/project/project';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Expose, Type } from 'class-transformer';
import { IRelatedAsset, RelatedAsset } from '../asset/asset';

export class Work extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.WORK;
  public assetId: string;
  public identifier: number;
  public action?: string;
  public state: string;
  public organizationId: string;
  public dataDate: string;

  @Expose({name: 'spaceIdsList'})
  public spaceIds: string[];

  @Expose({name: 'equipmentIdsList'})
  public equipmentIds: string[];

  @Expose({name: 'projectIdsList'})
  public projectIds: string[];

  public meetingId: string;

  @Type(() => Document) public documents: Document[] = [];
  @Type(() => Document) public pictures: Document[] = [];

  @Expose()
  public toString(): string {
    return this.computedProperties?.['displayName'] ?? `${this.identifier.toString()} - ${this.action}`;
  }

  /**
   * Get the related asset inside the Work computed properties.
   * @return List of related assets
   */
  @Expose()
  public get relatedAssets(): RelatedAsset[] {
    return this.computedProperties?.['asset']
        ?.map((asset: IRelatedAsset) => new RelatedAsset(asset))
      ?? [];
  }

  // Properties to be renamed
  protected static inputMapping = {
    asset: {input: ['computedProperties', 'asset', 'id'], output: 'assetId'},
    spaces: {input: ['computedProperties', 'spaces'], output: 'spaceIds'},
    equipments: {
      input: ['computedProperties', 'equipments'],
      output: 'equipmentIds',
      transform: (equipments: RelatedEquipment[]): string[] => equipments.map(e => e.id)
    },
    projects: {
      input: ['computedProperties', 'projects'],
      output: 'projectIds',
      transform: (projects: RelatedProject[]): string[] => projects.map(p => p.id)
    },
    meeting: {input: ['computedProperties', 'meeting', 'id'], output: 'meetingId'}
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    // Transform input, mapping keys as defined in the inputMapping above
    for (const inputMappingElement in Work.inputMapping) {
      const inputMapping = Work.inputMapping[inputMappingElement];
      if (hasProperty(obj, inputMapping.input)) {
        const source = getValue(obj, inputMapping.input);
        if (inputMapping.transform) {
          setValue(obj, [inputMapping.output], inputMapping.transform(source));
        } else {
          setValue(obj, [inputMapping.output], source);
        }
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Work.inputMapping) {
      delete obj[Work.inputMapping[inputMappingElement].input.first()];
    }
  }
}

export interface WorkInput {
  action?: string;
  state?: string;
  assetId?: string;
  meetingId?: string;
  spaceIds?: string[];
  equipmentIds?: string[];
  projectIds?: string[];
  dataDate?: string;
  properties?: Record<string, any>;
}
